import { useCallback, useEffect, useMemo, useState } from 'react';

import { App, Card } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Layout from 'components/Layout/Layout';

import RouteReportBlockedState from 'scenes/RouteReport/components/RouteReportBlockedState';
import { useRouteReportScope } from 'scenes/RouteReport/hooks/useRouteReportScope';
import { useRouteReportTheme } from 'scenes/RouteReport/hooks/useRouteReportTheme';
import { getUserTimeZone } from 'scenes/RouteReport/utils/time';

import VisitHistoryFilters from './components/VisitHistoryFilters';
import VisitHistoryHeader from './components/VisitHistoryHeader';
import VisitHistoryKpis from './components/VisitHistoryKpis';
import {
  VisitHistoryEmptyRange,
  VisitHistoryErrorState,
} from './components/VisitHistoryStates';
import VisitIncidencesCard from './components/VisitIncidencesCard';
import VisitMapAndRoutes from './components/VisitMapAndRoutes';
import VisitMaterialCards from './components/VisitMaterialCards';
import VisitSelectionChip from './components/VisitSelectionChip';
import VisitVansTable from './components/VisitVansTable';
import VisitsByDayChart from './components/VisitsByDayChart';
import { useVisitHistory } from './hooks/useVisitHistory';
import { useVisitHistoryExport } from './hooks/useVisitHistoryExport';
import { buildDayPoints } from './utils/charts';
import {
  VisitHistoryFilterState,
  isRangeTooLong,
  narrowDistributors,
  parseVisitHistoryQuery,
  pruneDistributorIds,
  pruneZoneIds,
  serializeVisitHistoryQuery,
  zoneOptions,
} from './utils/filters';
import { visitHistoryQueryKey } from './utils/queryKey';
import { enumerateDays } from './utils/range';
import {
  VisitSelection,
  aggregateStops,
  stopMatchesSelection,
} from './utils/selection';
import { describeSelection } from './utils/selectionLabel';
import { selectionScope, toggleSelection } from './utils/selectionScope';
import { visitPaletteVars } from './utils/stateColors';

import './VisitHistory.scss';

function VisitHistory() {
  const { t } = useTranslation();
  const scope = useRouteReportScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const userTimeZone = useMemo(() => getUserTimeZone(), []);
  // Same brand variables as the route report (`--rr-*`); semantic colors stay
  // fixed constants in the SCSS.
  const themeVars = useRouteReportTheme();

  // Filters live in the URL so a view can be shared and survives a reload.
  // The clock is read once per URL change: the default range is a day boundary.
  const filters = useMemo(
    () => parseVisitHistoryQuery(searchParams, dayjs(), userTimeZone),
    [searchParams, userTimeZone]
  );

  const { message } = App.useApp();

  // A URL range over the 31-day limit is already clamped in `filters`: write
  // the clamped values back (replace, not push) and say so without blocking.
  useEffect(() => {
    if (!isRangeTooLong(searchParams)) return;
    setSearchParams(serializeVisitHistoryQuery(filters), { replace: true });
    message.warning({
      key: 'visit-history-range',
      content: t('scenes.visit_history.filters.range_clamped', {
        from: dayjs(filters.range.from).format('DD/MM/YYYY'),
        to: dayjs(filters.range.to).format('DD/MM/YYYY'),
      }),
    });
  }, [searchParams, filters, setSearchParams, message, t]);

  const [selection, setSelection] = useState<VisitSelection>(null);

  const updateFilters = useCallback(
    (next: VisitHistoryFilterState) => {
      setSelection(null);
      setSearchParams(serializeVisitHistoryQuery(next), { replace: true });
    },
    [setSearchParams]
  );

  const zones = useMemo(
    () =>
      zoneOptions(scope.distributors, t('scenes.visit_history.zone_unnamed')),
    [scope.distributors, t]
  );
  // Ids from the URL only travel when the scoped distributors offer them.
  const zoneIds = useMemo(
    () => pruneZoneIds(scope.distributors, filters.zoneIds),
    [scope.distributors, filters.zoneIds]
  );
  const distributors = useMemo(
    () => narrowDistributors(scope.distributors, zoneIds),
    [scope.distributors, zoneIds]
  );

  // A single-distributor role is fixed; otherwise only the ids still offered
  // by the chosen zones travel, so a stale URL id never reaches the API.
  const distributorIds = useMemo(
    () =>
      scope.singleDistributor
        ? scope.distributors.map((d) => d.id)
        : pruneDistributorIds(
            scope.distributors,
            zoneIds,
            filters.distributorIds
          ),
    [scope.singleDistributor, scope.distributors, zoneIds, filters]
  );

  const query = useMemo(
    () =>
      scope.loading || scope.blockedReason
        ? undefined
        : {
            from: filters.range.from,
            to: filters.range.to,
            zoneIds,
            distributorIds,
          },
    [scope.loading, scope.blockedReason, filters, zoneIds, distributorIds]
  );

  // One source: the key is derived from the query the hook fetches with.
  const queryKey = visitHistoryQueryKey(query);

  const { loading, refreshing, summary, stops, lastUpdatedAt, error, refresh } =
    useVisitHistory(query);

  // The selection governs every block: with one active, all totals are
  // recomputed from the flat stops so they always add up; otherwise the server
  // KPIs are used as they came.
  const kpis = useMemo(() => {
    if (!summary) return null;
    if (!selection) return summary.kpis;

    return aggregateStops(
      stops.filter((stop) => stopMatchesSelection(stop, selection)),
      userTimeZone
    ).kpis;
  }, [summary, stops, selection, userTimeZone]);

  const dimScope = useMemo(
    () => selectionScope(stops, selection),
    [stops, selection]
  );
  const days = useMemo(
    () => enumerateDays(filters.range.from, filters.range.to),
    [filters.range]
  );
  const dayPoints = useMemo(
    () => buildDayPoints(days, summary?.byDay ?? []),
    [days, summary]
  );
  // Clicking what is already selected clears it.
  const select = useCallback(
    (next: NonNullable<VisitSelection>) =>
      setSelection((current) => toggleSelection(current, next)),
    []
  );

  const description = useMemo(
    () => describeSelection(selection, stops),
    [selection, stops]
  );

  const zoneNames = useMemo(
    () => zones.filter((z) => zoneIds.includes(z.id)).map((z) => z.name),
    [zones, zoneIds]
  );
  const distributorNames = useMemo(
    () =>
      distributors
        .filter((d) => distributorIds.includes(d.id))
        .map((d) => d.tradeName),
    [distributors, distributorIds]
  );
  const { canDownload, exporting, download } = useVisitHistoryExport({
    summary,
    stops,
    selection,
    description,
    range: filters.range,
    zoneNames,
    distributorNames,
    timeZone: userTimeZone,
  });

  if (scope.loading) {
    return (
      <Layout title="">
        <Card loading />
      </Layout>
    );
  }

  if (scope.blockedReason) {
    return (
      <Layout title="">
        <RouteReportBlockedState reason={scope.blockedReason} />
      </Layout>
    );
  }

  const emptyRange = !loading && summary !== null && summary.kpis.routes === 0;

  return (
    <Layout title="">
      <div
        className="visit-history"
        style={{ ...themeVars, ...visitPaletteVars }}
      >
        <VisitHistoryHeader
          canDownload={canDownload}
          exporting={exporting}
          onDownload={download}
        />

        <VisitHistoryFilters
          range={filters.range}
          onRangeChange={(range) => updateFilters({ ...filters, range })}
          zones={zones}
          zoneIds={zoneIds}
          onZoneChange={(nextZoneIds) =>
            updateFilters({
              ...filters,
              zoneIds: nextZoneIds,
              // Drop distributors the new zones no longer offer.
              distributorIds: pruneDistributorIds(
                scope.distributors,
                nextZoneIds,
                filters.distributorIds
              ),
            })
          }
          distributors={distributors}
          distributorIds={distributorIds}
          onDistributorChange={(ids) =>
            updateFilters({ ...filters, distributorIds: ids })
          }
          singleDistributor={scope.singleDistributor}
          loading={loading}
          refreshing={refreshing}
          onRefresh={refresh}
          lastUpdatedAt={lastUpdatedAt}
          userTimeZone={userTimeZone}
        />

        {error ? (
          <VisitHistoryErrorState onRetry={refresh} />
        ) : emptyRange ? (
          <VisitHistoryEmptyRange />
        ) : (
          <>
            <VisitSelectionChip
              description={description}
              onClear={() => setSelection(null)}
            />

            <VisitHistoryKpis kpis={kpis} loading={loading} />

            {(summary?.excludedRoutes ?? 0) > 0 && (
              <div className="visit-history-note">
                {t('scenes.visit_history.excluded', {
                  count: summary?.excludedRoutes,
                })}
              </div>
            )}

            {loading || !summary ? (
              <Card loading />
            ) : (
              <>
                <div className="visit-history-day-row">
                  <VisitsByDayChart
                    points={dayPoints}
                    scope={dimScope}
                    onSelectDay={(date) => select({ kind: 'day', date })}
                  />
                  <VisitIncidencesCard
                    stops={stops}
                    selection={selection}
                    onSelect={select}
                  />
                </div>
                <VisitMapAndRoutes
                  stops={stops}
                  queryKey={queryKey}
                  routesTotal={summary.kpis.routes}
                  selection={selection}
                  onSelect={select}
                  timeZone={userTimeZone}
                />
                <VisitMaterialCards
                  summary={summary}
                  stops={stops}
                  days={days}
                  selection={selection}
                  scope={dimScope}
                  onSelect={select}
                  timeZone={userTimeZone}
                />
                <VisitVansTable
                  summary={summary}
                  stops={stops}
                  selection={selection}
                  onSelect={select}
                  timeZone={userTimeZone}
                />
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default VisitHistory;
